/**
 * Photos on the device, before they leave it: downscale to at most 2000 px on the long side as JPEG, and
 * an optional "straighten" step — find a sheet of paper's four corners automatically (bright paper on a
 * darker surface, or the reverse), let the person move the corners by hand, then warp the quadrilateral
 * flat. Our own small code (no OpenCV.js: jscanify needs its 8 MB runtime — docs/DECISIONS.md D-0.4.0-10).
 * The math is pure (tested in vitest); only the canvas glue needs a browser.
 */

export type Pt = { x: number; y: number };
/** Corners in order: top-left, top-right, bottom-right, bottom-left. */
export type Quad = [Pt, Pt, Pt, Pt];

export const MAX_SIDE = 2000;
export const JPEG_QUALITY = 0.85;

export function fitWithin(w: number, h: number, max = MAX_SIDE): { w: number; h: number } {
  const s = Math.min(1, max / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * s)), h: Math.max(1, Math.round(h * s)) };
}

/**
 * The paper's corners in a small grayscale image (values 0–255, row-major), or null when nothing
 * stands out from the border clearly enough. Pixels that differ from the border's mean brightness by more
 * than `delta` form the mask; the corners are its extreme points along the two diagonals — which finds a
 * tilted sheet as well as a straight one.
 */
export function detectQuad(gray: ArrayLike<number>, w: number, h: number, delta = 38): Quad | null {
  if (w < 8 || h < 8) return null;
  let sum = 0;
  let n = 0;
  const b = Math.max(1, Math.round(Math.min(w, h) * 0.04));
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (x < b || y < b || x >= w - b || y >= h - b) {
        sum += gray[y * w + x]!;
        n++;
      }
    }
  }
  const border = sum / n;
  let tl: Pt | null = null;
  let tr: Pt | null = null;
  let br: Pt | null = null;
  let bl: Pt | null = null;
  let count = 0;
  for (let y = b; y < h - b; y++) {
    for (let x = b; x < w - b; x++) {
      if (Math.abs(gray[y * w + x]! - border) <= delta) continue;
      count++;
      if (!tl || x + y < tl.x + tl.y) tl = { x, y };
      if (!br || x + y > br.x + br.y) br = { x, y };
      if (!tr || x - y > tr.x - tr.y) tr = { x, y };
      if (!bl || y - x > bl.y - bl.x) bl = { x, y };
    }
  }
  if (!tl || !tr || !br || !bl) return null;
  const q: Quad = [tl, tr, br, bl];
  const area = quadArea(q);
  // Too small, or the mask is mostly noise (a real sheet fills most of its own outline).
  if (area < w * h * 0.15 || count < area * 0.6) return null;
  return q;
}

export function quadArea(q: Quad): number {
  let a = 0;
  for (let i = 0; i < 4; i++) {
    const p = q[i]!;
    const r = q[(i + 1) % 4]!;
    a += p.x * r.y - r.x * p.y;
  }
  return Math.abs(a) / 2;
}

/** The corners a person starts from when nothing was detected: a small inset of the whole photo. */
export function insetQuad(w: number, h: number, f = 0.06): Quad {
  return [
    { x: w * f, y: h * f },
    { x: w * (1 - f), y: h * f },
    { x: w * (1 - f), y: h * (1 - f) },
    { x: w * f, y: h * (1 - f) },
  ];
}

/** The output size of a flattened quad: its average side lengths, capped at MAX_SIDE. */
export function quadSize(q: Quad): { w: number; h: number } {
  const d = (a: Pt, c: Pt) => Math.hypot(a.x - c.x, a.y - c.y);
  const w = (d(q[0], q[1]) + d(q[3], q[2])) / 2;
  const h = (d(q[0], q[3]) + d(q[1], q[2])) / 2;
  return fitWithin(Math.max(1, w), Math.max(1, h));
}

/**
 * The 3×3 homography (row-major, h33 = 1) taking the rectangle (0,0)–(w,h) onto the quad — so every
 * output pixel knows where to read from in the photo.
 */
export function homography(w: number, h: number, q: Quad): number[] {
  const src: Pt[] = [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
  const A: number[][] = [];
  const B: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i]!;
    const { x: u, y: v } = q[i]!;
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    B.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    B.push(v);
  }
  return [...solve(A, B), 1];
}

/** Gaussian elimination with partial pivoting (8×8). */
function solve(A: number[][], B: number[]): number[] {
  const n = B.length;
  const M = A.map((r, i) => [...r, B[i]!]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r]![c]!) > Math.abs(M[p]![c]!)) p = r;
    [M[c], M[p]] = [M[p]!, M[c]!];
    const pv = M[c]![c]!;
    if (Math.abs(pv) < 1e-12) throw new Error('image: the corners do not make a shape that can be flattened');
    for (let k = c; k <= n; k++) M[c]![k]! /= pv;
    for (let r = 0; r < n; r++) {
      if (r === c) continue;
      const f = M[r]![c]!;
      if (!f) continue;
      for (let k = c; k <= n; k++) M[r]![k]! -= f * M[c]![k]!;
    }
  }
  return M.map((r) => r[n]!);
}

export function applyH(H: number[], x: number, y: number): Pt {
  const d = H[6]! * x + H[7]! * y + H[8]!;
  return { x: (H[0]! * x + H[1]! * y + H[2]!) / d, y: (H[3]! * x + H[4]! * y + H[5]!) / d };
}

/** Flattens the quad of an RGBA image into a w×h RGBA image (bilinear sampling). */
export function warp(src: { data: ArrayLike<number>; width: number; height: number }, q: Quad, w: number, h: number): Uint8ClampedArray<ArrayBuffer> {
  const H = homography(w, h, q);
  const out = new Uint8ClampedArray(new ArrayBuffer(w * h * 4));
  const sw = src.width;
  const sh = src.height;
  const s = src.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = applyH(H, x + 0.5, y + 0.5);
      const fx = Math.min(sw - 1.001, Math.max(0, p.x - 0.5));
      const fy = Math.min(sh - 1.001, Math.max(0, p.y - 0.5));
      const x0 = Math.floor(fx);
      const y0 = Math.floor(fy);
      const ax = fx - x0;
      const ay = fy - y0;
      const i00 = (y0 * sw + x0) * 4;
      const i10 = i00 + 4;
      const i01 = i00 + sw * 4;
      const i11 = i01 + 4;
      const o = (y * w + x) * 4;
      for (let c = 0; c < 4; c++) {
        out[o + c] = (s[i00 + c]! * (1 - ax) + s[i10 + c]! * ax) * (1 - ay) + (s[i01 + c]! * (1 - ax) + s[i11 + c]! * ax) * ay;
      }
    }
  }
  return out;
}

/* ---------------------------------------------------------------------------------------------
 * Browser glue
 * ------------------------------------------------------------------------------------------- */

export async function loadBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
    } catch {
      /* older Safari: fall back to an <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

const sizeOf = (img: ImageBitmap | HTMLImageElement) => ({ w: 'naturalWidth' in img ? img.naturalWidth : img.width, h: 'naturalHeight' in img ? img.naturalHeight : img.height });

export function canvasOf(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

export function toJpeg(c: HTMLCanvasElement, quality = JPEG_QUALITY): Promise<Blob> {
  return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('image: could not make a JPEG'))), 'image/jpeg', quality));
}

/** The photo drawn at ≤ 2000 px (the canvas it can be straightened from, and the JPEG that is sent). */
export async function prepare(file: Blob): Promise<{ canvas: HTMLCanvasElement; blob: Blob }> {
  const img = await loadBitmap(file);
  const { w, h } = sizeOf(img);
  const fit = fitWithin(w, h);
  const canvas = canvasOf(fit.w, fit.h);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, fit.w, fit.h);
  ctx.drawImage(img, 0, 0, fit.w, fit.h);
  if ('close' in img) img.close();
  return { canvas, blob: await toJpeg(canvas) };
}

/** Auto-detect on a ~240 px copy; the corners come back in the canvas's own pixels. */
export function autoCorners(canvas: HTMLCanvasElement): Quad | null {
  const s = Math.min(1, 240 / Math.max(canvas.width, canvas.height));
  const w = Math.max(8, Math.round(canvas.width * s));
  const h = Math.max(8, Math.round(canvas.height * s));
  const small = canvasOf(w, h);
  const ctx = small.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(canvas, 0, 0, w, h);
  const px = ctx.getImageData(0, 0, w, h).data;
  const gray = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) gray[i] = (px[i * 4]! * 299 + px[i * 4 + 1]! * 587 + px[i * 4 + 2]! * 114) / 1000;
  const q = detectQuad(gray, w, h);
  return q ? (q.map((p) => ({ x: p.x / s, y: p.y / s })) as Quad) : null;
}

export async function straighten(canvas: HTMLCanvasElement, q: Quad): Promise<{ canvas: HTMLCanvasElement; blob: Blob }> {
  const { w, h } = quadSize(q);
  const src = canvas.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, canvas.width, canvas.height);
  const data = warp(src, q, w, h);
  const out = canvasOf(w, h);
  out.getContext('2d')!.putImageData(new ImageData(data, w, h), 0, 0);
  return { canvas: out, blob: await toJpeg(out) };
}
